import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import ClaudeDesignBundleUpload from './ClaudeDesignBundleUpload';
import { isPhase25Complete } from '../lib/constants';

// UXV.F1 (#1422) — sidebar card that surfaces the (previously orphaned) Claude
// Design bundle uploader on the project dashboard. Mirrors the branding /
// Claude-Design-export override cards (rounded-xl, CSS-var inline styles).
//
// DISC.B2 (#1477) — the card now leads with a 5-stage tracker for the Claude
// Design round-trip so the whole workflow is visible as a unit and the user
// can see current progress. The "Import Claude Design bundle" upload button is
// preserved below the tracker. All stage state is derived client-side from the
// artefact list the dashboard already holds — no new endpoint.
//
// Project-scoped: the upload endpoint is
//   POST /projects/:projectId/claude-design/upload
// so this MUST live on a page that has a concrete projectId in scope
// (ProjectDashboardPage), not the cross-project ArtefactsPage.
//
// Ingest is NOT gated by the F4 export toggle — bundle import is always
// available (only the F2 export action respects the toggle).

const BUNDLE_TYPE = 'claude_design_bundle';

interface BundleArtefact {
  id: string;
  type: string;
  status: string;
  /** Artefact creation timestamp — used as the bundle "imported" date (Stage 4). */
  createdAt?: string;
}

interface Props {
  projectId: string;
  /** The dashboard's already-fetched artefact list, for client-side stage derivation. */
  artefacts: ReadonlyArray<BundleArtefact>;
  /** Fired after a successful ingest so the parent can refresh artefacts. */
  onIngested?: () => void;
}

const cardCls = 'rounded-xl border shadow-sm transition-shadow duration-150';
const cardStyle: CSSProperties = {
  background: 'var(--surface)', borderColor: 'var(--border)', padding: 'var(--space-5)',
};

type StageState = 'pending' | 'active' | 'done';

// Dot colour per state: grey (not reached) / amber (manual step in progress) /
// green (BluePrint can confirm it).
const DOT_COLOR: Record<StageState, string> = {
  pending: '#CBD5E1',
  active:  'var(--warning)',
  done:    'var(--success)',
};

function StageRow({ state, title, testId, children }: { state: StageState; title: string; testId: string; children: React.ReactNode }) {
  return (
    <li data-testid={testId} data-state={state} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
      <span
        aria-hidden
        style={{
          flexShrink: 0, width: 8, height: 8, borderRadius: '50%',
          background: DOT_COLOR[state], marginTop: 5,
        }}
      />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <span style={{
          fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
          color: state === 'pending' ? 'var(--text-secondary)' : 'var(--text)',
        }}>
          {title}
        </span>
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
          {children}
        </span>
      </span>
    </li>
  );
}

function formatImportDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ClaudeDesignBundleImportCard({ projectId, artefacts, onIngested }: Props) {
  const [open, setOpen] = useState(false);

  // Stage derivation — all client-side from the artefact list (#1477).
  // artefacts are newest-first (GET /artefacts orders by updatedAt desc) —
  // find() returns the latest artefact of each type (the current screen_spec /
  // the most recently imported bundle), which is the correct selection here.
  const stage1Ready = isPhase25Complete(artefacts);
  const screenSpecId = artefacts.find(a => a.type === 'screen_spec')?.id;
  const bundle = artefacts.find(a => a.type === BUNDLE_TYPE);
  const bundleImported = Boolean(bundle);
  const bundleApproved = bundle?.status === 'approved';
  const importDate = formatImportDate(bundle?.createdAt);

  return (
    <div className={cardCls} style={cardStyle} data-testid="claude-design-bundle-card">
      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: '0 0 6px' }}>
        Claude Design bundle
      </h3>
      <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
        Claude Design can refine your Phase 2.5 screen specs into production-ready visual designs.
        Export your specs from the Screen Specifications artefact, design in Claude Design, then
        import the handoff bundle here to bring refined components and the design brief back into
        BluePrint.
      </p>

      {/* DISC.B2 — round-trip stage tracker. Stages 2 & 3 are manual Claude
          Design steps BluePrint can't directly observe: amber ("in progress")
          once Stage 1 is ready, then green once a bundle is imported — a
          successful import is proof those two steps demonstrably happened, so
          the tracker reaches a fully-complete state. */}
      <ol
        data-testid="claude-design-stage-tracker"
        style={{ listStyle: 'none', margin: '0 0 14px', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        <StageRow testId="stage-1" state={stage1Ready ? 'done' : 'pending'} title="1. Export">
          {stage1Ready
            ? (screenSpecId
                ? <Link to={`/artefacts/${screenSpecId}`} data-testid="stage-export-link" style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>Export to Claude Design →</Link>
                : 'Export to Claude Design')
            : 'Approve all 5 Phase 2.5 artefacts first'}
        </StageRow>

        <StageRow testId="stage-2" state={bundleImported ? 'done' : stage1Ready ? 'active' : 'pending'} title="2. Design">
          Paste PROMPT.md into a Claude Design project
        </StageRow>

        <StageRow testId="stage-3" state={bundleImported ? 'done' : stage1Ready ? 'active' : 'pending'} title="3. Package">
          Export the handoff bundle ZIP from Claude Design
        </StageRow>

        <StageRow testId="stage-4" state={bundleImported ? 'done' : 'pending'} title="4. Import">
          {bundleImported
            ? <span data-testid="stage-import-done">Bundle imported{importDate ? ` · ${importDate}` : ''}</span>
            : 'Import your Claude Design bundle below'}
        </StageRow>

        <StageRow testId="stage-5" state={bundleApproved ? 'done' : 'pending'} title="5. Verified">
          {bundleApproved
            ? <span data-testid="stage-verified-done" style={{ color: 'var(--success)', fontWeight: 600 }}>✓ Bundle approved — PROMPT.md active in Dev Agent</span>
            : 'Awaiting bundle approval'}
        </StageRow>
      </ol>

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 500,
            padding: '6px 14px', color: 'white', background: 'var(--primary)',
            border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
          }}
        >
          Import Claude Design bundle
        </button>
      ) : (
        <ClaudeDesignBundleUpload projectId={projectId} onUploaded={() => onIngested?.()} />
      )}
    </div>
  );
}
